import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';

import { IonicModule } from '@ionic/angular/lazy';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BaseComponent } from '@app/core/base/base-component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-form-textarea',
  templateUrl: './form-textarea.component.html',
  styleUrls: ['./form-textarea.component.scss'],
  standalone: true,
  imports: [IonicModule, ReactiveFormsModule],
})
export class FormTextareaComponent extends BaseComponent {
  @Input() label: string = '';
  // 日本語コメント: アクセシビリティ対応のための aria-label
  @Input() ariaLabel: string = '';
  // 日本語コメント: Enter キーのヒントを OS キーボードに表示
  @Input() enterKeyhint: string | null = null;
  // 日本語コメント: Enter キー押下を親へ通知
  @Output() enterKey = new EventEmitter<void>();
  @Input() controlName: string = '';
  @Input() form!: FormGroup;
  @Input() errors: { [key: string]: string } = {};
  @Input() rows: number = 3;
  @Input() autoGrow: boolean = true;
  /** autoGrow 時の最大行数（未指定時は制限なし） */
  @Input() maxRows: number | null = null;
  // 追加: プレースホルダー / 最大文字数 / カウンター / ion-item の lines
  @Input() placeholder: string = '';
  @Input() maxlength = 1000;
  @Input() counter: boolean = false;
  @Input() lines: 'full' | 'inset' | 'none' = 'none';
  // デフォルトのフォームコントロール（フォームまたはコントロール名が無効な場合に使用）
  private readonly defaultControl = new FormControl();

  /**
   * 入力に関連付けられたフォームコントロールを返します。
   * FormControl型として返すことで[formControl]ディレクティブと互換性を持たせます。
   * フォームまたはコントロール名が無効な場合はデフォルトのコントロールを返します。
   */
  get control(): FormControl {
    if (!this.form || !this.controlName) {
      return this.defaultControl as FormControl;
    }
    const control = this.form.get(this.controlName);
    return (control as FormControl) || (this.defaultControl as FormControl);
  }

  /**
   * フォームコントロールが有効かどうかを確認します（サーバーエラー時も表示）。
   */
  get isInvalid(): boolean {
    const control = this.control;
    if (!control?.invalid) return false;
    return !!(control.touched || control.errors?.['serverError']);
  }

  /**
   * フォームコントロールのエラーメッセージの配列を返します（サーバーエラーを優先表示）。
   */
  get errorMessages(): string[] {
    const messages: string[] = [];
    const control = this.control;
    const errors = control?.errors;

    if (errors) {
      if (typeof errors['serverErrorMessage'] === 'string') {
        messages.push(errors['serverErrorMessage']);
      }
      if (this.errors) {
        for (const errorKey in errors) {
          if (errorKey !== 'serverErrorMessage') {
            const message = this.errors?.[errorKey];
            if (message) messages.push(message);
          }
        }
      }
    }

    return messages;
  }

  // テンプレートから参照するカウンターフォーマッタ
  readonly counterFormatterRef = (inputLength: number, maxLength: number) => `${ maxLength - inputLength } characters remaining`;
}
